import { useCallback, useEffect, useRef } from "react";
import { fetchQuery, graphql } from "react-relay";
import relayEnvironment from "../../relayEnvironment";
import {
  UNKNOWN_VERDICT,
  type EmailValidationVerdict,
} from "./emailValidationCore";

// Shared client-side email validation wiring (B2, 2026-08-23) — layers the
// public validateEmailInput query (server/graphql/resolvers/
// emailValidationResolver.ts, #1799) on top of the existing local checks in
// inputValidation.tsx. Two tiers, cheapest first:
//   1. Local (emailValidationCore.ts's `localEmailHint`, re-exported below)
//      — the existing typo-map correction + non-deliverable-domain check.
//      Pure, sync, no network — safe to run on every keystroke. Split into
//      its own relay-free file so it's importable from the CRA jest harness
//      (see emailValidationCore.ts's header comment for why).
//   2. Remote (this file's `useEmailValidation`'s `checkEmail`) — a
//      debounced live call to the canonical engine (MX-checked,
//      deny-listed). Meant to be invoked from a blur handler or right
//      before a submit/next action, never per keystroke.
//
// A definitive negative (reason: syntax | junk_domain | no_mx) is the only
// "invalid" verdict — everything else (a corrected typo, an ambiguous MX
// result, a network failure, the 30/IP/10min rate limit) resolves "unknown"
// or "valid" so a flaky endpoint can never trap a real lead. See
// server/schema/_emailValidation.graphql for the full contract.

export type {
  EmailValidationStatus,
  EmailValidationReason,
  EmailValidationVerdict,
} from "./emailValidationCore";
export { localEmailHint } from "./emailValidationCore";

const DEBOUNCE_MS = 500;

const CHECK_QUERY = graphql`
  query useEmailValidationCheckQuery($email: String!) {
    validateEmailInput(email: $email) {
      ok
      normalized
      suggestion
      reason
    }
  }
`;

/**
 * Returns a debounced `checkEmail(email)` function that resolves the live
 * server verdict. Debounces + sequences so a burst of calls (e.g. a blur
 * immediately followed by a submit) only fires one live request, and a
 * superseded or failed check resolves "unknown" (fail open) rather than
 * hanging or rejecting — callers never need a .catch().
 */
export function useEmailValidation(): (email: string) => Promise<EmailValidationVerdict> {
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingResolveRef = useRef<((verdict: EmailValidationVerdict) => void) | null>(null);

  // Unmount cleanup: cancel the debounce timer and settle any still-pending
  // caller "unknown" (fail open) — a blur-then-fast-navigate otherwise leaves
  // the timer to fire a wasted request against the rate-limited endpoint.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pendingResolveRef.current) {
        pendingResolveRef.current(UNKNOWN_VERDICT);
        pendingResolveRef.current = null;
      }
    },
    [],
  );

  const checkEmail = useCallback((email: string): Promise<EmailValidationVerdict> => {
    const trimmed = (email || "").trim();
    if (!trimmed) return Promise.resolve(UNKNOWN_VERDICT);

    if (timerRef.current) clearTimeout(timerRef.current);
    // A still-pending prior call is about to be superseded — resolve it
    // "unknown" now rather than leaving that caller's promise hanging
    // forever (its setTimeout will never fire once we clear it above).
    if (pendingResolveRef.current) {
      pendingResolveRef.current(UNKNOWN_VERDICT);
      pendingResolveRef.current = null;
    }

    const seq = ++seqRef.current;
    return new Promise<EmailValidationVerdict>((resolve) => {
      pendingResolveRef.current = resolve;
      timerRef.current = setTimeout(() => {
        fetchQuery<any>(relayEnvironment, CHECK_QUERY, { email: trimmed })
          .toPromise()
          .then((data: any) => {
            if (seqRef.current !== seq) return; // superseded elsewhere
            pendingResolveRef.current = null;
            const result = data?.validateEmailInput;
            if (!result) {
              resolve(UNKNOWN_VERDICT);
              return;
            }
            resolve({
              status: result.ok ? "valid" : "invalid",
              suggestion: result.suggestion ?? null,
              reason: result.reason ?? null,
            });
          })
          .catch(() => {
            // Network blip or the 30/IP/10min rate limit — fail open.
            if (seqRef.current === seq) {
              pendingResolveRef.current = null;
              resolve(UNKNOWN_VERDICT);
            }
          });
      }, DEBOUNCE_MS);
    });
  }, []);

  return checkEmail;
}
